import React, { ElementType, FC, useMemo, CSSProperties } from "react";

import { Placement } from "@popperjs/core";
import classnames from "classnames";
import { Moment } from "moment";

import { SelectDateTarget } from "./SelectDateTarget";
import { StyledSelectDate, StyledSelectDateButton } from "./Styles";
import { DaysToModify } from "./types";
import { formatDate } from "../../utils/dateUtils";
import {
  ChangeMode,
  DatePickerV2,
  TimestampDateRange,
  toMoment,
} from "../DatePicker/DatePickerV2";

const isDayInRange = (day: Date, data: DaysToModify): boolean => {
  if (data.repeating) {
    const from = new Date(
      day.getFullYear(),
      data.from.getMonth(),
      data.from.getDate()
    );
    const to = new Date(
      day.getFullYear(),
      data.to.getMonth(),
      data.to.getDate()
    );
    return day >= from && day <= to;
  }

  return day >= data.from && day <= data.to;
};

type TriggerStringType = "text" | "icon" | "inline";
type TriggerFunctionType = (text: string) => JSX.Element;

export interface ISelectDateV2 {
  /** Change mode - can be atomic and instant */
  changeMode?: "atomic" | "instant";
  /** Trigger - can be text, icon, inline, or custom render function */
  trigger?: TriggerStringType | TriggerFunctionType;
  /** Save button text */
  saveButtonText?: string;
  /** Cancel button text */
  cancelButtonText?: string;
  /** Clear button text */
  clearButtonText?: string;
  /** Modal header text */
  modalHeaderText?: string;
  /** Modal cancel button text */
  modalCancelBtnText?: string;
  /** Modal discard button text */
  modalDiscardBtnText?: string;
  /** Modal discard message text */
  modalDiscardMessage?: string;
  /** Tooltip text */
  tooltipText?: string;
  /** Called when day is clicked */
  onDayClick?: (day: Moment, modifiers: string[]) => void;
  /** Save changes in atomic mode */
  onSave?: (dates?: TimestampDateRange) => void;
  /** On cancel closes datepicker or opens discard modal in atomic mode */
  onCancel?: () => void;
  /** Clear dates when they are not required */
  onClear?: () => void;
  /** Use this callback to get is picker visible */
  onToggleDatePicker?: (value: boolean) => void;
  /** Set selected day or days */
  selectedDays?: TimestampDateRange;
  /** Set month */
  defaultMonth?: Moment;
  /** Set selection range */
  selectionMode?: ChangeMode;
  /** Set is date required */
  required?: boolean;
  /** Set default show on date picker (only for text and icon mode) */
  defaultShowDatePicker?: boolean;
  /** Long date format (Always show year) */
  longDateFormat?: boolean;
  /** Set is date required */
  defaultTimezoneAware?: boolean;
  /** Set first day of week */
  firstDayOfWeek?: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  /** One of icon types */
  icon?: ElementType;
  /** Default Label text */
  defaultLabelText?: string;
  /** Date format */
  dateFormat?: string;
  /** Menu classes */
  menuClassName?: string;
  /** Target element classes */
  targetClassName?: string;
  /** Target element text classes */
  targetTextClassName?: string;
  /** Target element icon classes */
  targetIconClassName?: string;
  /** Background style for clickable element after the menu is open (opacity, color, etc...) */
  backgroundElementClass?: string;
  /** Days to modify */
  daysToModify?: DaysToModify[];
  /** Array of weekend days */
  weekends?: number[];
  /** Weekend label */
  weekendLabel?: string;
  /** Array of nonworking week days */
  nonWorkingDaysOfWeek?: number[];
  /** Nonworking week days label */
  nonWorkingDaysOfWeekLabel?: string;
  /** Allows weekends to be selected */
  weekendIsSelectable?: boolean;
  /** Popper Tooltip style */
  popperTooltipStyle?: CSSProperties;
  /** Popper Tooltip class name*/
  popperTooltipClassName?: string;
  /** Popper class name*/
  popperClassName?: string;
  /** Position of selectDate menu */
  position?: Placement;
  /** Disable days before specified date */
  disableDaysBefore?: Moment;
  /** Enable year selector */
  enableYearPicker?: boolean;
  /** Disable aniamtions */
  disableAnimations?: boolean;
}

export const SelectDateV2: FC<ISelectDateV2> = ({
  changeMode: mode = "instant",
  trigger: labelType = "text",
  saveButtonText = "Save",
  cancelButtonText = "Cancel",
  clearButtonText = "Clear",
  modalHeaderText = "Discard changes?",
  modalDiscardMessage = "All unsaved changes will be lost.",
  modalDiscardBtnText = "OK",
  modalCancelBtnText = "Cancel",
  onDayClick,
  onSave,
  onCancel,
  onToggleDatePicker,
  required: dateRequired = false,
  longDateFormat = false,
  defaultShowDatePicker = false,
  firstDayOfWeek = 0,
  selectedDays,
  selectionMode = "custom",
  menuClassName,
  targetClassName,
  icon,
  defaultLabelText = "Set...",
  targetTextClassName,
  targetIconClassName,
  backgroundElementClass,
  dateFormat,
  defaultMonth,
  daysToModify = [],
  weekends = [],
  weekendLabel = "Weekend",
  nonWorkingDaysOfWeek = [],
  nonWorkingDaysOfWeekLabel = "Unavailable",
  weekendIsSelectable = false,
  tooltipText,
  popperTooltipClassName,
  popperClassName,
  popperTooltipStyle,
  position,
  disableDaysBefore,
  enableYearPicker,
  disableAnimations,
}) => {
  const labelText = useMemo(() => {
    if (!selectedDays) {
      return defaultLabelText;
    } else if (!selectedDays.from && !selectedDays.to) {
      return defaultLabelText;
    } else {
      const formattedEndDate = selectedDays.to
        ? formatDate(toMoment(selectedDays.to), dateFormat, longDateFormat)
        : "";
      const formattedStartDate = selectedDays.from
        ? formatDate(toMoment(selectedDays?.from), dateFormat, longDateFormat)
        : "";

      if (
        selectedDays.to &&
        selectedDays.from &&
        toMoment(selectedDays.from).isSame(toMoment(selectedDays.to))
      ) {
        return formattedEndDate;
      }

      return `${formattedStartDate} - ${formattedEndDate}`;
    }
  }, [selectedDays, dateFormat, defaultLabelText, longDateFormat]);

  const modifiers = useMemo(() => {
    const userAvailabilities = daysToModify.filter(
      (data) => data.type === "user_day_off"
    );
    const globalDaysOff = daysToModify.filter(
      (data) => data.type === "global_day_off"
    );
    const selectableGlobalDaysOff = daysToModify.filter(
      (data) => data.type === "selectable_global_day_off"
    );

    return {
      userAvailability: (day: Date) => {
        const currentDay = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate()
        );
        const matchedData = userAvailabilities.find((data) =>
          isDayInRange(currentDay, data)
        );
        return {
          matched: !!matchedData,
          title: matchedData ? matchedData.title : null,
        };
      },
      weekend: (day: Date) => {
        const isWeekend = weekends.includes(day.getDay());
        return {
          matched: isWeekend,
          title: isWeekend ? weekendLabel : null,
        };
      },
      nonWorkingDay: (day: Date) => {
        if (weekends.includes(day.getDay())) {
          return { matched: true, title: "" };
        }
        const nonWorkingDays = [...selectableGlobalDaysOff, ...globalDaysOff];
        const currentDay = new Date(
          day.getFullYear(),
          day.getMonth(),
          day.getDate()
        );
        const matchedData = nonWorkingDays.find((data) =>
          isDayInRange(currentDay, data)
        );
        return {
          matched: !!matchedData,
          title: matchedData ? matchedData.title : null,
        };
      },
      nonWorkingDaysOfWeek: (day: Date) => {
        const isNonWorkingDay = nonWorkingDaysOfWeek.includes(day.getDay());
        return {
          matched: isNonWorkingDay,
          title: isNonWorkingDay ? nonWorkingDaysOfWeekLabel : null,
        };
      },
    };
  }, [
    daysToModify,
    nonWorkingDaysOfWeek,
    nonWorkingDaysOfWeekLabel,
    weekendLabel,
    weekends,
  ]);

  const renderTargetEl = useMemo(() => {
    if (labelType === "icon" && icon) {
      return (
        <SelectDateTarget
          icon={icon}
          title={tooltipText}
          targetIconClassName={targetIconClassName}
          popperTooltipClassName={popperTooltipClassName}
          popperTooltipStyle={popperTooltipStyle}
        />
      );
    }
    if (typeof labelType === "function") {
      return labelType(labelText);
    }
    return (
      <StyledSelectDateButton
        type="button"
        className={classnames("date-picker-target", targetClassName)}
      >
        <span className={targetTextClassName}>{labelText}</span>
      </StyledSelectDateButton>
    );
  }, [
    icon,
    labelText,
    labelType,
    popperTooltipClassName,
    popperTooltipStyle,
    targetClassName,
    targetIconClassName,
    targetTextClassName,
    tooltipText,
  ]);

  return (
    <StyledSelectDate className="select-date">
      {labelType !== "inline" ? (
        <DatePickerV2
          target={renderTargetEl}
          position={position}
          menuClassName={menuClassName}
          popperClassName={popperClassName}
          month={defaultMonth}
          instant={mode === "instant"}
          mode={selectionMode}
          required={dateRequired}
          selected={selectedDays}
          firstDayOfWeek={firstDayOfWeek}
          saveLabel={saveButtonText}
          cancelLabel={cancelButtonText}
          clearLabel={clearButtonText}
          onSave={onSave}
          onClose={onCancel}
          modifiers={modifiers}
          modalHeaderText={modalHeaderText}
          modalDiscardMessage={modalDiscardMessage}
          modalDiscardBtnText={modalDiscardBtnText}
          modalCancelBtnText={modalCancelBtnText}
          disabledDaysBefore={disableDaysBefore}
          backgroundElementClass={backgroundElementClass}
          disabled={weekendIsSelectable ? [] : [0, 6]}
          popperTooltipClassName={popperTooltipClassName}
          popperTooltipStyle={popperTooltipStyle}
          open={defaultShowDatePicker}
          onCalendarToggle={onToggleDatePicker}
          onDayClick={onDayClick}
          enableConfirmModal={mode === "atomic" && !dateRequired}
          disableYearPicker={!enableYearPicker}
          disableAnimations={disableAnimations}
          showControls
        />
      ) : (
        <DatePickerV2
          month={defaultMonth}
          instant={mode === "instant"}
          mode={selectionMode}
          required={dateRequired}
          selected={selectedDays}
          firstDayOfWeek={firstDayOfWeek}
          saveLabel={saveButtonText}
          cancelLabel={cancelButtonText}
          clearLabel={clearButtonText}
          disabledDaysBefore={disableDaysBefore}
          popperTooltipClassName={popperTooltipClassName}
          popperTooltipStyle={popperTooltipStyle}
          disabled={weekendIsSelectable ? [] : [0, 6]}
          onSave={onSave}
          onChange={onSave}
          modifiers={modifiers}
          onDayClick={onDayClick}
          disableYearPicker={!enableYearPicker}
          disableAnimations={disableAnimations}
          showControls
          open
        />
      )}
    </StyledSelectDate>
  );
};

SelectDateV2.displayName = "SelectDateV2";
